import { createTheme } from '@mui/material/styles';

// Color palette
const colors = {
  // Primary Blueish Colors
  primary: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6', // Main primary color
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  // Neutral colors
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
  // Success colors
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  // Error colors
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  // Warning colors
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
};

// Light theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary[500],
      light: colors.primary[400],
      dark: colors.primary[600],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.gray[600],
      light: colors.gray[400],
      dark: colors.gray[800],
      contrastText: '#ffffff',
    },
    success: {
      main: colors.success[500],
      light: colors.success[400],
      dark: colors.success[600],
    },
    error: {
      main: colors.error[500],
      light: colors.error[400],
      dark: colors.error[600],
    },
    warning: {
      main: colors.warning[500],
      light: colors.warning[400],
      dark: colors.warning[600],
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: colors.gray[900],
      secondary: colors.gray[600],
      disabled: colors.gray[400],
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.875rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
    '0 0 0 1px rgba(0, 0, 0, 0.05)',
  ],
});

// Dark theme with glass/neon effects
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primary[400],
      light: colors.primary[300],
      dark: colors.primary[500],
      contrastText: '#000000',
    },
    secondary: {
      main: colors.gray[400],
      light: colors.gray[300],
      dark: colors.gray[500],
      contrastText: '#000000',
    },
    success: {
      main: colors.success[400],
      light: colors.success[300],
      dark: colors.success[500],
    },
    error: {
      main: colors.error[400],
      light: colors.error[300],
      dark: colors.error[500],
    },
    warning: {
      main: colors.warning[400],
      light: colors.warning[300],
      dark: colors.warning[500],
    },
    background: {
      default: '#0a0a0a',
      paper: 'rgba(17, 24, 39, 0.8)',
    },
    text: {
      primary: '#f9fafb',
      secondary: '#d1d5db',
      disabled: '#6b7280',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.875rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(59, 130, 246, 0.1)',
  ],
});

// CSS Variables for theme integration
export const themeCSSVariables = {
  light: {
    '--primary-50': colors.primary[50],
    '--primary-100': colors.primary[100],
    '--primary-200': colors.primary[200],
    '--primary-300': colors.primary[300],
    '--primary-400': colors.primary[400],
    '--primary-500': colors.primary[500],
    '--primary-600': colors.primary[600],
    '--primary-700': colors.primary[700],
    '--primary-800': colors.primary[800],
    '--primary-900': colors.primary[900],
    '--gray-50': colors.gray[50],
    '--gray-100': colors.gray[100],
    '--gray-200': colors.gray[200],
    '--gray-300': colors.gray[300],
    '--gray-400': colors.gray[400],
    '--gray-500': colors.gray[500],
    '--gray-600': colors.gray[600],
    '--gray-700': colors.gray[700],
    '--gray-800': colors.gray[800],
    '--gray-900': colors.gray[900],
    '--success-500': colors.success[500],
    '--error-500': colors.error[500],
    '--warning-500': colors.warning[500],
    '--bg-default': '#f8fafc',
    '--bg-paper': '#ffffff',
    '--bg-surface': '#ffffff',
    '--bg-surface-secondary': '#f9fafb',
    '--bg-surface-tertiary': '#f3f4f6',
    '--text-primary': colors.gray[900],
    '--text-secondary': colors.gray[600],
    '--text-disabled': colors.gray[400],
    '--border-primary': colors.gray[200],
    '--border-secondary': colors.gray[300],
    '--border-focus': colors.primary[500],
  },
  dark: {
    '--primary-50': colors.primary[50],
    '--primary-100': colors.primary[100],
    '--primary-200': colors.primary[200],
    '--primary-300': colors.primary[300],
    '--primary-400': colors.primary[400],
    '--primary-500': colors.primary[500],
    '--primary-600': colors.primary[600],
    '--primary-700': colors.primary[700],
    '--primary-800': colors.primary[800],
    '--primary-900': colors.primary[900],
    '--gray-50': colors.gray[50],
    '--gray-100': colors.gray[100],
    '--gray-200': colors.gray[200],
    '--gray-300': colors.gray[300],
    '--gray-400': colors.gray[400],
    '--gray-500': colors.gray[500],
    '--gray-600': colors.gray[600],
    '--gray-700': colors.gray[700],
    '--gray-800': colors.gray[800],
    '--gray-900': colors.gray[900],
    '--success-500': colors.success[400],
    '--error-500': colors.error[400],
    '--warning-500': colors.warning[400],
    '--bg-default': '#0a0a0a',
    '--bg-paper': 'rgba(17, 24, 39, 0.8)',
    '--bg-surface': 'rgba(31, 41, 55, 0.8)',
    '--bg-surface-secondary': 'rgba(55, 65, 81, 0.6)',
    '--bg-surface-tertiary': 'rgba(75, 85, 99, 0.4)',
    '--text-primary': '#f9fafb',
    '--text-secondary': '#d1d5db',
    '--text-disabled': '#6b7280',
    '--border-primary': 'rgba(75, 85, 99, 0.5)',
    '--border-secondary': 'rgba(107, 114, 128, 0.3)',
    '--border-focus': colors.primary[400],
  },
};

export default { lightTheme, darkTheme, themeCSSVariables }; 